import { ListTodo } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useMyTasks, useSetTaskStatus, type Task, type TaskStatus } from "@/api/queries";
import { useMe } from "@/app/auth";
import { EmptyState } from "@/components/common/EmptyState";
import { PageHeader } from "@/components/common/PageHeader";
import { Alert } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Segmented } from "@/components/ui/segmented";
import { Skeleton } from "@/components/ui/skeleton";
import { errorText } from "@/lib/errors";
import { dateInZone } from "@/lib/format";

import { isOverdue } from "./lib";
import { TaskTable } from "./TaskTable";

type Filter = "open" | "done" | "all";

const filterStatuses: Record<Filter, TaskStatus[]> = {
  open: ["todo", "in_progress"],
  done: ["done"],
  all: [],
};

/** Tasks assigned to the caller across active projects. */
export function MyTasksPage() {
  const { t } = useTranslation();
  const me = useMe();
  const [filter, setFilter] = useState<Filter>("open");
  const tasks = useMyTasks(filterStatuses[filter]);
  const move = useSetTaskStatus();
  const today = dateInZone(new Date(), me.user.timezone);
  const projects = useMemo(() => new Map((tasks.data?.projects ?? []).map((p) => [p.id, p])), [tasks.data]);
  const items = tasks.data?.items ?? [];
  const overdue = items.filter((task) => isOverdue(task, today)).length;

  const onMove = (task: Task, status: TaskStatus) => move.mutate({ taskId: task.id, status });

  return (
    <>
      <PageHeader title={t("tasks.title")} description={t("tasks.subtitle")} />
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Segmented
          aria-label={t("tasks.filters.show")}
          value={filter}
          onChange={setFilter}
          options={[
            { value: "open", label: t("tasks.filters.open") },
            { value: "done", label: t("tasks.filters.done") },
            { value: "all", label: t("tasks.filters.all") },
          ]}
        />
        {tasks.data && (
          <span className="text-sm text-muted">
            {t("tasks.count", { count: items.length })}
            {overdue > 0 && (
              <span className="font-medium text-danger">{` · ${t("tasks.overdue")}: ${overdue}`}</span>
            )}
          </span>
        )}
      </div>

      {move.error && (
        <Alert variant="danger" className="mb-4">
          {errorText(t, move.error)}
        </Alert>
      )}

      {tasks.isPending ? (
        <Skeleton className="h-64" />
      ) : tasks.isError ? (
        <Alert variant="danger">{errorText(t, tasks.error)}</Alert>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={ListTodo}
            title={filter === "done" ? t("tasks.emptyMineDone") : t("tasks.emptyMine")}
          />
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <TaskTable tasks={items} today={today} projects={projects} canMove={() => true} onMove={onMove} />
        </Card>
      )}
    </>
  );
}
